// src/components/ui/drawer/CapacityDrawer.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Drawer, CircularProgress } from '@mui/material';
import { useNotification } from '../../../context/NotificationContext';
import { useLayout } from '../../../context/LayoutContext';
import { useSchedule } from '../../../hooks/vehicle/useSchedule';
import '../../../assets/scss/components/ui/drawner/CapacityDrawer.scss';
import { useQuery } from '@tanstack/react-query';
import { useCapacityService } from '../../../service/vehicle/capacityService';

const CapacityDrawer = ({
  open,
  onClose,
  operationId,
  operationName,
  currentYear,
  currentMonth,
  selectedRouteId = null // Nueva prop para soporte de rutas
}) => {
  const { theme } = useLayout();
  const { showNotification } = useNotification();

  // Usar el hook centralizado con soporte de rutas
  const {
    useMonthlyCapacity,
    setMonthlyCapacity,
    isLoading: isScheduleLoading
  } = useSchedule(operationId, selectedRouteId);

  const [activeTab, setActiveTab] = useState(0);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [capacityData, setCapacityData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const capacityService = useCapacityService();
  // Hook de capacidad mensual con soporte de rutas
  const { data: currentCapacity, isLoading: isLoadingCapacity } = useMonthlyCapacity(
    selectedYear,
    selectedMonth,
    { enabled: open && !!operationId }
  );

  // Hook de análisis de capacidad con soporte de rutas
  const { data: capacityAnalysis, isLoading: isLoadingAnalysis } = useQuery({
    queryKey: ['capacityAnalysis', operationId, selectedYear, selectedMonth, selectedRouteId],
    queryFn: () => {
      return capacityService.getCapacityAnalysisByRoute(
        operationId, 
        selectedYear, 
        selectedMonth,
        selectedRouteId
      );
    },
    enabled: open && !!operationId && activeTab === 1,
    staleTime: 5 * 60 * 1000,
  });

  // Inicializar datos cuando se carga la capacidad
  useEffect(() => {
    if (currentCapacity?.data) {
      const initialData = {};
      for (let day = 1; day <= 31; day++) {
        initialData[`day_${day}`] = currentCapacity.data[`day_${day}`] || 0;
      }
      setCapacityData(initialData);
    }
  }, [currentCapacity]);

  // Obtener días del mes con memoización
  const daysInMonth = useMemo(() => {
    return new Date(selectedYear, selectedMonth, 0).getDate();
  }, [selectedYear, selectedMonth]);

  // Manejar cambio de capacidad
  const handleCapacityChange = useCallback((day, value) => {
    const numValue = parseInt(value) || 0;
    if (numValue < 0 || numValue > 100) return;

    setCapacityData(prev => ({
      ...prev,
      [`day_${day}`]: numValue
    }));
    setHasChanges(true);
  }, []);

  // Aplicar capacidad uniforme
  const applyUniformCapacity = useCallback((value) => {
    const numValue = parseInt(value) || 0;
    if (numValue < 0 || numValue > 100) return;

    const newData = {};
    for (let day = 1; day <= daysInMonth; day++) {
      newData[`day_${day}`] = numValue;
    }
    setCapacityData(newData);
    setHasChanges(true);
  }, [daysInMonth]);

  // Aplicar patrón semanal
  const applyWeeklyPattern = useCallback((weekdayCapacity, weekendCapacity) => {
    const newData = {};
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(selectedYear, selectedMonth - 1, day);
      const dayOfWeek = date.getDay();
      // 0 = Domingo, 6 = Sábado
      newData[`day_${day}`] = (dayOfWeek === 0 || dayOfWeek === 6) 
        ? weekendCapacity 
        : weekdayCapacity;
    }
    setCapacityData(newData);
    setHasChanges(true);
  }, [daysInMonth, selectedYear, selectedMonth]);

  // Guardar cambios usando el hook
  const handleSave = useCallback(async () => {
  try {
    console.log('🔍 DEBUG - Datos antes de enviar:', {
      selectedRouteId,
      selectedYear,
      selectedMonth,
      capacityData
    });

    const saveData = {
      year: selectedYear,
      month: selectedMonth,
      capacities: capacityData,
      operationRouteId: selectedRouteId || null // ✅ Cambio crítico: usar operationRouteId
    };

    console.log('📤 DEBUG - Payload enviado:', saveData);

    await setMonthlyCapacity(saveData);

    showNotification({
      message: selectedRouteId 
        ? `Capacidad guardada para ruta específica`
        : 'Capacidad general guardada exitosamente',
      severity: 'success'
    });
    setHasChanges(false);
  } catch (error) {
    console.error('❌ Error en handleSave:', error);
    showNotification({
      message: 'Error al guardar capacidad: ' + (error.message || 'Error desconocido'),
      severity: 'error'
    });
  }
}, [selectedYear, selectedMonth, capacityData, selectedRouteId, setMonthlyCapacity, showNotification]);

  // Cambiar mes
  const handleMonthChange = useCallback((direction) => {
    let newMonth = selectedMonth + direction;
    let newYear = selectedYear;

    if (newMonth > 12) {
      newMonth = 1;
      newYear += 1;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear -= 1;
    }

    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
    setHasChanges(false);
  }, [selectedMonth, selectedYear]);

  // Obtener color según utilización
  const getUtilizationColor = useCallback((percentage) => {
    if (percentage > 100) return 'error';
    if (percentage >= 80) return 'warning';
    if (percentage >= 50) return 'success';
    return 'default';
  }, []);

  // Estados consolidados de carga
  const isLoading = isLoadingCapacity || isScheduleLoading;

  // Renderizar tab de configuración
  const renderConfigurationTab = () => (
    <div className={`GM__${theme}-capacity-configuration`}>
      {/* Indicador de ruta */}
      {selectedRouteId && (
        <div className={`GM__${theme}-route-indicator`}>
          <span className={`GM__${theme}-route-badge`}>
            📍 Configuración específica para ruta seleccionada
          </span>
        </div>
      )}

      {/* Controles rápidos */}
      <div className={`GM__${theme}-configuration-card`}>
        <h3 className={`GM__${theme}-card-title`}>Configuración Rápida</h3>
        <div className={`GM__${theme}-button-grid`}>
          <button 
            className={`GM__${theme}-config-button`}
            onClick={() => {
              const value = prompt('Ingrese capacidad para todos los días:');
              if (value !== null) applyUniformCapacity(value);
            }}
          >
            Aplicar Capacidad Uniforme
          </button>
          <button 
            className={`GM__${theme}-config-button`}
            onClick={() => {
              const weekday = prompt('Capacidad días de semana (L-V):');
              const weekend = prompt('Capacidad fines de semana (S-D):');
              if (weekday !== null && weekend !== null) {
                applyWeeklyPattern(parseInt(weekday), parseInt(weekend));
              }
            }}
          >
            Aplicar Patrón Semanal
          </button>
        </div>
      </div>

      {/* Calendario de capacidades */}
      <div className={`GM__${theme}-configuration-card`}>
        <h3 className={`GM__${theme}-card-title`}>
          Capacidad por Día - {new Date(selectedYear, selectedMonth - 1).toLocaleString('es', { month: 'long', year: 'numeric' })}
          {selectedRouteId && <span className={`GM__${theme}-route-suffix`}> (Ruta específica)</span>}
        </h3>
        
        <div className={`GM__${theme}-day-grid`}>
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
            const date = new Date(selectedYear, selectedMonth - 1, day);
            const dayName = date.toLocaleString('es', { weekday: 'short' });
            const capacity = capacityData[`day_${day}`] || 0;
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;

            return (
              <div 
                key={day} 
                className={`GM__${theme}-day-item ${isWeekend ? 'weekend' : ''}`}
              >
                <div className={`GM__${theme}-day-label`}>
                  {dayName} {day}
                </div>
                <input
                  type="number"
                  value={capacity}
                  onChange={(e) => handleCapacityChange(day, e.target.value)}
                  className={`GM__${theme}-capacity-input`}
                  min="0"
                  max="100"
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Renderizar tab de análisis
  const renderAnalysisTab = () => (
    <div className={`GM__${theme}-capacity-analysis`}>
      {isLoadingAnalysis ? (
        <div className={`GM__${theme}-loading-container`}>
          <CircularProgress />
        </div>
      ) : capacityAnalysis?.data ? (
        <>
          {/* Indicador de ruta en análisis */}
          {selectedRouteId && (
            <div className={`GM__${theme}-analysis-route-info`}>
              <span className={`GM__${theme}-route-badge`}>
                📊 Análisis específico para ruta seleccionada
              </span>
            </div>
          )}

          {/* Resumen */}
          <div className={`GM__${theme}-analysis-card`}>
            <h3 className={`GM__${theme}-card-title`}>
              Resumen del Mes
            </h3>
            <div className={`GM__${theme}-summary-grid`}>
              <div className={`GM__${theme}-summary-item`}>
                <div className={`GM__${theme}-summary-value`}>
                  {capacityAnalysis.data.summary?.totalPlannedCapacity || 0}
                </div>
                <div className={`GM__${theme}-summary-label`}>Capacidad Total</div>
              </div>
              <div className={`GM__${theme}-summary-item`}>
                <div className={`GM__${theme}-summary-value`}>
                  {capacityAnalysis.data.summary?.totalActualVehicles || 0}
                </div>
                <div className={`GM__${theme}-summary-label`}>Vehículos Programados</div>
              </div>
              <div className={`GM__${theme}-summary-item`}>
                <div className={`GM__${theme}-summary-value error`}>
                  {capacityAnalysis.data.summary?.daysOverCapacity || 0}
                </div>
                <div className={`GM__${theme}-summary-label`}>Días Sobre Capacidad</div>
              </div>
              <div className={`GM__${theme}-summary-item`}>
                <div className={`GM__${theme}-summary-value warning`}>
                  {capacityAnalysis.data.summary?.daysUnderCapacity || 0}
                </div>
                <div className={`GM__${theme}-summary-label`}>Días Bajo Capacidad</div>
              </div>
            </div>
          </div>

          {/* Tabla detallada */}
          <div className={`GM__${theme}-table-container`}>
            <table className={`GM__${theme}-capacity-table`}>
              <thead>
                <tr>
                  <th>Día</th>
                  <th>Capacidad</th>
                  <th>Programados</th>
                  <th>Cancelados</th>
                  <th>Diferencia</th>
                  <th>Utilización</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {capacityAnalysis.data.analysis?.map((day) => (
                  <tr key={day.day}>
                    <td>
                      {new Date(day.date).toLocaleDateString('es', { 
                        weekday: 'short', 
                        day: 'numeric' 
                      })}
                    </td>
                    <td>{day.plannedCapacity}</td>
                    <td>{day.actualCount}</td>
                    <td>{day.cancelledCount}</td>
                    <td>
                      <span 
                        className={`GM__${theme}-chip ${day.difference > 0 ? 'error' : day.difference < 0 ? 'warning' : 'success'}`}
                      >
                        {day.difference > 0 ? `+${day.difference}` : day.difference}
                      </span>
                    </td>
                    <td>
                      <div className={`GM__${theme}-progress-container`}>
                        <div 
                          className={`GM__${theme}-progress-bar ${getUtilizationColor(parseFloat(day.percentageUsed))}`}
                          style={{ width: `${Math.min(parseFloat(day.percentageUsed), 100)}%` }}
                        ></div>
                        <span className={`GM__${theme}-progress-text`}>
                          {day.percentageUsed}%
                        </span>
                      </div>
                    </td>
                    <td>
                      {day.status === 'over' && (
                        <span className={`GM__${theme}-status-chip error`}>
                          <i className={`GM__${theme}-status-icon`}>⚠</i>
                          Excedido
                        </span>
                      )}
                      {day.status === 'under' && (
                        <span className={`GM__${theme}-status-chip warning`}>
                          Bajo
                        </span>
                      )}
                      {day.status === 'exact' && (
                        <span className={`GM__${theme}-status-chip success`}>
                          Exacto
                        </span>
                      )}
                      {day.status === 'no_capacity' && (
                        <span className={`GM__${theme}-status-chip default`}>
                          Sin Config
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className={`GM__${theme}-alert info`}>
          {selectedRouteId 
            ? 'No hay datos de análisis disponibles para esta ruta en este período'
            : 'No hay datos de análisis disponibles para este período'
          }
        </div>
      )}
    </div>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className={`GM__capacity-drawer-container-${theme}`}
      PaperProps={{
        className: `GM__${theme}-capacity-drawer-paper`,
        sx: { width: { xs: '100%', md: '80%', lg: '70%' } }
      }}
    >
      <div className={`GM__${theme}-capacity-drawer`}>
        {/* Header */}
        <div className={`GM__${theme}-capacity-drawer-header`}>
          <div className={`GM__${theme}-drawer-title-container`}>
            <i className={`GM__${theme}-drawer-title-icon`}>📅</i>
            <h2 className={`GM__${theme}-drawer-title`}>
              Gestión de Capacidad - {operationName || 'Operación'}
              {selectedRouteId && (
                <span className={`GM__${theme}-route-indicator`}>
                  - Ruta Específica
                </span>
              )}
            </h2>
          </div>
          <div className={`GM__${theme}-drawer-header-actions`}>
            {hasChanges && (
              <button
                className={`GM__${theme}-save-button`}
                onClick={handleSave}
                disabled={isLoading}
              >
                <i className={`GM__${theme}-button-icon`}>💾</i>
                Guardar Cambios
              </button>
            )}
            <button 
              className={`GM__${theme}-close-button`} 
              onClick={onClose}
            >
              <i className={`GM__${theme}-button-icon`}>✕</i>
            </button>
          </div>
        </div>

        <div className={`GM__${theme}-drawer-divider`}></div>

        {/* Navegación de meses */}
        <div className={`GM__${theme}-month-navigation`}>
          <button 
            className={`GM__${theme}-nav-button`} 
            onClick={() => handleMonthChange(-1)}
          >
            ←
          </button>
          <h3 className={`GM__${theme}-month-title`}>
            {new Date(selectedYear, selectedMonth - 1).toLocaleString('es', { 
              month: 'long', 
              year: 'numeric' 
            })}
          </h3>
          <button 
            className={`GM__${theme}-nav-button`} 
            onClick={() => handleMonthChange(1)}
          >
            →
          </button>
        </div>

        <div className={`GM__${theme}-drawer-divider`}></div>

        {/* Tabs */}
        <div className={`GM__${theme}-tabs`}>
          <button 
            className={`GM__${theme}-tab-button ${activeTab === 0 ? 'active' : ''}`}
            onClick={() => setActiveTab(0)}
          >
            <i className={`GM__${theme}-tab-icon`}>📅</i>
            Configuración
          </button>
          <button 
            className={`GM__${theme}-tab-button ${activeTab === 1 ? 'active' : ''}`}
            onClick={() => setActiveTab(1)}
          >
            <i className={`GM__${theme}-tab-icon`}>📈</i>
            Análisis
          </button>
        </div>

        {/* Contenido */}
        <div className={`GM__${theme}-capacity-drawer-content`}>
          {isLoading ? (
            <div className={`GM__${theme}-loading-container`}>
              <CircularProgress />
              <div className={`GM__${theme}-loading-message`}>
                Cargando datos de capacidad...
              </div>
            </div>
          ) : (
            <>
              {activeTab === 0 && renderConfigurationTab()}
              {activeTab === 1 && renderAnalysisTab()}
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default CapacityDrawer;